type UuidCrypto = Pick<Crypto, 'getRandomValues'> & { randomUUID?: () => string };

function formatUuidV4(bytes: Uint8Array): string {
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** RFC 4122 v4 from `getRandomValues`, which (unlike `randomUUID`) exists on plain-http origins. */
export function uuidFromRandomValues(source: Pick<Crypto, 'getRandomValues'>): string {
  return formatUuidV4(source.getRandomValues(new Uint8Array(16)));
}

/**
 * Last resort when the runtime has no Web Crypto global at all. `Math.random` is not a
 * CSPRNG; any environment that exposes `crypto` never reaches this.
 */
function uuidFromMathRandom(): string {
  const bytes = Uint8Array.from({ length: 16 }, () => Math.floor(Math.random() * 256));
  return formatUuidV4(bytes);
}

function ambientCrypto(): UuidCrypto | undefined {
  if (typeof globalThis.crypto === 'undefined') return undefined;
  return globalThis.crypto;
}

/**
 * A v4 UUID in any browser. `crypto.randomUUID` only exists in secure contexts (https,
 * localhost) and iOS Safari 15.4+, so a LAN or older-device origin falls back to
 * `getRandomValues`. A runtime with no `crypto` global at all uses `Math.random` with
 * the same version and variant bits — never a non-UUID string, which the API's uuid
 * columns reject, and never a `ReferenceError` from reading a missing global.
 */
export function uuid(source?: UuidCrypto): string {
  const resolved = source ?? ambientCrypto();

  if (!resolved) {
    return uuidFromMathRandom();
  }

  if (resolved.randomUUID) {
    return resolved.randomUUID();
  }

  return uuidFromRandomValues(resolved);
}
